import React, { useState } from 'react'
import {
  DropdownDataItem,
  DropdownOptionGroup,
  DropdownProps
} from './Dropdown.type'

const isOptionGroup = (item: DropdownDataItem): item is DropdownOptionGroup => {
  return 'options' in item
}

export const Dropdown = (props: DropdownProps) => {
  const {
    name,
    id,
    placeholder,
    label,
    reference,
    callback,
    data,
    error,
    message,
    helpText,
    disabled,
    className,
    testId
  } = props

  const [selectedValue, setSelectedValue] = useState<string>('')
  const helpListId = id ? `${id}-error-help` : undefined
  const isError = Boolean(error)
  const hasErrorMessage = isError && Boolean(message)
  const hasHelpText = Boolean(helpText)
  const shouldDescribeSelect = hasErrorMessage || hasHelpText

  const onSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextValue = event.target.value
    setSelectedValue(nextValue)
    callback?.(nextValue)
  }

  return (
    <div
      className={`Dropdown-group ${className}`}
      data-testid={testId ? `${testId}-group` : undefined}
    >
      <label
        className="Form-label"
        htmlFor={id}
        data-testid={testId ? `${testId}-label` : undefined}
      >
        {label}
      </label>

      <div
        className={['Dropdown', isError ? 'Dropdown--error' : '']
          .filter(Boolean)
          .join(' ')}
        data-testid={testId ? `${testId}-container` : undefined}
      >
        <select
          className="Dropdown-menu"
          id={id}
          name={name}
          ref={reference}
          value={selectedValue}
          onChange={onSelectChange}
          disabled={disabled}
          aria-invalid={isError ? 'true' : undefined}
          aria-describedby={shouldDescribeSelect ? helpListId : undefined}
          data-testid={testId ? `${testId}-select` : undefined}
        >
          <option value="">{placeholder || 'Select'}</option>
          {data?.map((item, index) => {
            if (isOptionGroup(item)) {
              return (
                <optgroup
                  label={item.label}
                  key={`group-${item.label}-${index}`}
                >
                  {item.options.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.value}
                    </option>
                  ))}
                </optgroup>
              )
            }

            return (
              <option key={item.key} value={item.key}>
                {item.value}
              </option>
            )
          })}
        </select>
      </div>

      {(hasErrorMessage || hasHelpText) && (
        <ul
          id={helpListId}
          data-testid={testId ? `${testId}-help-list` : undefined}
        >
          {hasErrorMessage && (
            <li
              className="Form-help Form-help--error"
              data-testid={testId ? `${testId}-message` : undefined}
            >
              <span
                data-testid={testId ? `${testId}-warning` : undefined}
                aria-hidden="true"
              >
                warning
              </span>
              <span className="u-hiddenVisually">Error,</span>
              <span>{message}</span>
            </li>
          )}
          {hasHelpText && <li className="Form-help">{helpText}</li>}
        </ul>
      )}
    </div>
  )
}
